import { LoadingOverlay, logger } from "cayo.ui";
import { IWizardCommonProps, Wizard, WizardPage } from "cayo.ui.wizards";
import { useContext, useEffect, useReducer, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { ProductContext } from "../../contexts/product-context";
import {
  createDomainsAndPartitions,
  getAgents,
  getForest,
  validateAccountName,
  validateCred,
  waitForAgentOnline,
} from "./api";
import { wizardMessages } from "./messages";
import { sortRecords } from "./service";
import ADConnector from "./steps/ADConnector";
import AuditMonitoring from "./steps/AuditMonitoring";
import ChooseConnection from "./steps/ChooseConnection";
import CreateConnectionAccounts from "./steps/CreateConnectionAccounts";
import DomainsAndPartitions from "./steps/DomainsAndPartitions";
import DownloadAdConnectorConfig from "./steps/DownloadAdConnectorConfig";
import GetAdConnector from "./steps/GetAdConnector";
import SelectDomainsAndPartitions from "./steps/SelectDomainsAndPartitions";
import SpecifyCredentials from "./steps/SpecifyCredentials";
import wizardStateReducer, { InitialState } from "./store";

const log = logger.getLogger("NewDomainAndPartitionWizard");

interface IProps extends IWizardCommonProps {
  isInitial: boolean;
}

export const NewDomainAndPartitionWizard = (props: IProps) => {
  const product = useContext(ProductContext);
  const intl = useIntl();
  const [wizardState, dispatchWizardState] = useReducer(wizardStateReducer, InitialState);
  const stateRef = useRef(InitialState);
  const wizardRef = useRef<{ resetError: () => void }>(null);
  const [loadingText, setLoadingText] = useState<undefined | string>(undefined);
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [agents, setAgents] = useState([]);
  const [adChecked, setAdChecked] = useState(true);
  const [signedIn, setSignedIn] = useState(false);
  const [adDownloaded, setAdDownloaded] = useState<boolean>(false);
  const [agentIsOnline, setAgentIsOnline] = useState<boolean | undefined>(undefined);
  const [checkingAdStatus, setCheckingAdStatus] = useState<boolean>(false);
  const [firstAd, setFirstAd] = useState([]);
  stateRef.current = wizardState;

  const credentialsData = stateRef.current.credentials;
  const credentialsFilled = !!(
    credentialsData.name &&
    credentialsData.password &&
    credentialsData.user
  );
  const creationFailed =
    wizardState.creationResult?.failed?.length > 0 && !wizardState.creationResult?.managed?.length;
  const managedAccountName = wizardState?.creationForm?.managedAccountName;

  const isSpecifyCredHidden = wizardState.creationForm.credentialUsage !== "userAccount";
  const [specifyCredIsValid, setSpecifyCredIsValid] = useState(false);
  useEffect(() => {
    setSpecifyCredIsValid(!isSpecifyCredHidden && credentialsFilled && !creationFailed);
  }, [isSpecifyCredHidden, credentialsFilled, creationFailed]);

  useEffect(() => {
    setLoadingText("Loading");
    Promise.all([getForest(), getAgents()])
      .then(([forest, agentsData]) => {
        dispatchWizardState({
          type: "update",
          payload: {
            form: "credentials",
            data: {
              name: forest?.forestName || "",
            },
          },
        });
        const agents = (agentsData || []) as [];
        setAgents(agents);
      })
      .finally(() => {
        setLoadingText(undefined);
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    product?.isHosted &&
      dispatchWizardState({
        type: "update",
        payload: {
          form: "creationForm",
          data: {
            credentialUsage: "managedServiceAccount",
          },
        },
      });
  }, [product?.isHosted]);

  const onConfigDownload = async () => {
    setCheckingAdStatus(true);
    try {
      const ad = await waitForAgentOnline();
      const agents = (ad || []) as [];
      setAgentIsOnline(!!ad);
      setFirstAd(agents);
    } catch (e) {
      log.error("Error while waiting for agent to be online", e);
      setAgentIsOnline(false);
    } finally {
      setCheckingAdStatus(false);
    }
  };

  if (isLoading) {
    return <LoadingOverlay />;
  }

  return (
    <Wizard
      loadingText={loadingText}
      title={intl.formatMessage(wizardMessages.wizardTitle)}
      isFinished={isFinished}
      activePage={0}
      ref={wizardRef}
      closeConfirmationMessage={intl.formatMessage(wizardMessages.closeConfirmation)}
      showStepError
      onActiveStepChanged={(name) => {
        if (
          wizardState.creationResult?.failed?.length &&
          name !== intl.formatMessage(wizardMessages.managedDomainsAndPartitions) &&
          name !== intl.formatMessage(wizardMessages.monitoringAudit)
        ) {
          dispatchWizardState({
            type: "update",
            payload: {
              form: "creationResult",
              data: {
                ...(wizardState.creationResult as object),
                failed: [],
              },
            },
          });
        }
      }}
      {...props}
    >
      <WizardPage
        isHidden={!product?.isHosted || agents.length >= 1}
        isInputValid={adDownloaded}
        onNext={() => Promise.resolve()}
        title={intl.formatMessage(wizardMessages.adConnectorDownLoad)}
        stepName={intl.formatMessage(wizardMessages.adConnectorDownLoad)}
        subTitle=""
      >
        <GetAdConnector setAdDownloaded={setAdDownloaded} />
      </WizardPage>

      <WizardPage
        isHidden={!product?.isHosted || agents.length >= 1}
        isInputValid={agentIsOnline}
        onNext={() => Promise.resolve()}
        title={intl.formatMessage(wizardMessages.downloadAdConnectorConfigTitle)}
        stepName={intl.formatMessage(wizardMessages.downloadAdConnectorConfigTitle)}
        subTitle=""
      >
        <DownloadAdConnectorConfig
          agentIsOnline={agentIsOnline}
          checkingAdStatus={checkingAdStatus}
          onConfigDownload={onConfigDownload}
        />
      </WizardPage>

      <WizardPage
        isHidden={!product?.isHosted && agents.length < 1}
        isInputValid={!creationFailed && (!adChecked || wizardState.creationForm.node)}
        onNext={() => Promise.resolve()}
        title={intl.formatMessage(wizardMessages.adConnectorStepName)}
        stepName={intl.formatMessage(wizardMessages.adConnectorStepName)}
        subTitle=""
      >
        <ADConnector
          isHosted={!!product?.isHosted}
          onClose={props.onClose}
          agents={agents}
          firstAd={firstAd}
          adChecked={adChecked}
          setAdChecked={setAdChecked}
          state={wizardState}
          dispatch={dispatchWizardState}
          isInitial={!!props?.isInitial}
        />
      </WizardPage>

      <WizardPage
        isHidden={!!product?.isHosted}
        isInputValid={!creationFailed}
        onNext={() => Promise.resolve()}
        title={intl.formatMessage(wizardMessages.connectionStepName)}
        stepName={intl.formatMessage(wizardMessages.connectionStepName)}
        subTitle=""
      >
        <ChooseConnection dispatch={dispatchWizardState} />
      </WizardPage>

      <WizardPage
        isHidden={wizardState.creationForm.credentialUsage === "userAccount"}
        isInputValid={signedIn && managedAccountName && !creationFailed}
        onNext={() => {
          const data = {
            accountName: stateRef.current.creationForm.managedAccountName,
            forestName: stateRef.current.credentials.name,
            user: stateRef.current.credentials.user,
            password: stateRef.current.credentials.password,
            node: stateRef.current.credentials.agentId,
          };
          return validateAccountName(data);
        }}
        title={intl.formatMessage(wizardMessages.createConnectionAccounts)}
        stepName={intl.formatMessage(wizardMessages.createConnectionAccounts)}
        subTitle={intl.formatMessage(wizardMessages.createConnectionAccountsSub)}
      >
        <CreateConnectionAccounts
          isHosted={!!product?.isHosted}
          state={wizardState}
          setSignedIn={setSignedIn}
          dispatch={dispatchWizardState}
          credentialsFilled={credentialsFilled}
          handleLoading={setLoadingText}
        />
      </WizardPage>

      <WizardPage
        isHidden={isSpecifyCredHidden}
        isInputValid={specifyCredIsValid}
        onNext={() =>
          validateCred(stateRef.current.credentials).then((res) => {
            const partitionData = res?.partitions || [];
            dispatchWizardState({
              type: "add-partitions",
              payload: {
                partitions: sortRecords(partitionData.filter((item) => item.hasAdminRights)),
                partitionsNoPermission: partitionData.filter((item) => !item.hasAdminRights),
              },
            });
          })
        }
        title={intl.formatMessage(wizardMessages.specifyCredentialsMain)}
        stepName={intl.formatMessage(wizardMessages.specifyCredentialsMain)}
        subTitle={intl.formatMessage(wizardMessages.specifyCredentialsSubTitle)}
      >
        <SpecifyCredentials state={wizardState} dispatch={dispatchWizardState} />
      </WizardPage>

      <WizardPage
        isInputValid={wizardState.selectedPartitions.length && !creationFailed}
        onNext={() => Promise.resolve()}
        title={intl.formatMessage(wizardMessages.selectDomainsAndPartitions)}
        stepName={intl.formatMessage(wizardMessages.selectDomainsAndPartitions)}
        subTitle={""}
      >
        <SelectDomainsAndPartitions state={wizardState} dispatch={dispatchWizardState} />
      </WizardPage>

      <WizardPage
        isInputValid={!creationFailed}
        onNext={() =>
          createDomainsAndPartitions(stateRef.current).then((response) => {
            if (response?.managed?.length) {
              setIsFinished(true);
            }
            dispatchWizardState({
              type: "update",
              payload: {
                form: "creationResult",
                data: {
                  ...(response as object),
                },
              },
            });
          })
        }
        title={intl.formatMessage(wizardMessages.monitoringAudit)}
        stepName={intl.formatMessage(wizardMessages.monitoringAudit)}
        subTitle={
          <div>
            {intl.formatMessage(wizardMessages.monitoringAuditDescription)}{" "}
            <a
              href="https://support.cayosoft.com/hc/en-us/articles/36184019034253"
              target="_blank"
              rel="noreferrer"
            >
              {intl.formatMessage(wizardMessages.LearnMore)}.
            </a>
          </div>
        }
      >
        <AuditMonitoring
          state={wizardState}
          isHosted={!!product?.isHosted}
          dispatch={dispatchWizardState}
        />
      </WizardPage>

      <WizardPage
        isInputValid={!creationFailed}
        onNext={() => Promise.resolve()}
        title={intl.formatMessage(wizardMessages.managedDomainsAndPartitions)}
        stepName={intl.formatMessage(wizardMessages.managedDomainsAndPartitions)}
        subTitle={""}
      >
        <DomainsAndPartitions
          state={wizardState}
          dispatch={dispatchWizardState}
          setLoadingText={setLoadingText}
        />
      </WizardPage>
    </Wizard>
  );
};
